import axios from 'axios';
import { schoolCache } from '../services/cacheService';
import { districtCache } from '../services/districtCacheService';
import config from '../config';

// Constants
const VERSION_KEY = 'app_version';
const RELEASE_VERSION_KEY = 'app_release_version';
const TECHNICAL_VERSION_KEY = 'app_technical_version';
const LAST_CHECK_KEY = 'last_version_check';
const LAST_UPDATE_KEY = 'last_update_time';
const UPDATE_COOLDOWN = 5000; // 5 seconds
// Use environment-specific check intervals:
// - Production: 5 minutes (frequent polling; visibility/focus checks handle the common case)
// - Other environments: 1 hour (less frequent to reduce server load during development)
const CHECK_INTERVAL = config.environment === 'production' 
  ? 5 * 60 * 1000   // 5 minutes for production
  : 60 * 60 * 1000; // 1 hour for staging/development
const UPDATE_TIMEOUT = 30000; // 30 second timeout for update
const CACHE_CLEAR_TIMEOUT = 5000; // Cap on how long cache clearing may delay the reload

// State tracking
let checkInProgress = false;
let checkPromise = null;
let versionCheckInterval = null;

// Resolves with the wrapped promise, or after `ms` if it never settles. Used to
// keep IndexedDB work off the critical path of applying an update.
function withTimeout(promise, ms) {
  return Promise.race([
    promise,
    new Promise((resolve) => setTimeout(resolve, ms))
  ]);
}

/**
 * Check if the application needs to be updated
 * @param {boolean} force Force check regardless of cooldown
 * @returns {Promise<boolean>} True if update is in progress
 */
export async function checkAppVersion(force = false) {
  // Avoid concurrent checks
  if (checkInProgress && !force) {
    return checkPromise;
  }
  
  // Skip checking if we recently updated (prevents flickering)
  const lastUpdate = localStorage.getItem(LAST_UPDATE_KEY);
  if (lastUpdate && Date.now() - parseInt(lastUpdate) < UPDATE_COOLDOWN && !force) {
    return false;
  }
  
  // Skip frequent checks (1-minute minimum between checks)
  const lastCheck = localStorage.getItem(LAST_CHECK_KEY);
  if (!force && lastCheck && Date.now() - parseInt(lastCheck) < 60 * 1000) {
    return false;
  }
  
  // Handle local development (no overlay on localhost to avoid double overlay after reload)
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    if (!localStorage.getItem(VERSION_KEY)) {
      localStorage.setItem(VERSION_KEY, 'v1.0.0-dev-local');
    }
    return false;
  }
  
  // Set check in progress
  checkInProgress = true;
  localStorage.setItem(LAST_CHECK_KEY, Date.now().toString());
  
  // Create promise for the check
  checkPromise = (async () => {
    try {
      // Use v2 endpoint exclusively with cache-busting timestamp
      // The timestamp query parameter ensures CDN and browser caches are bypassed
      const response = await axios.get(`${config.apiUrl}/v2/system/version?t=${Date.now()}`);
      
      const { version } = response.data;

      // Parse version info (could be "2024.12.03|v1.2.1-staging-abc1234" or just "v1.2.1")
      let releaseVersion, technicalVersion;
      if (version.includes('|')) {
        [releaseVersion, technicalVersion] = version.split('|');
      } else {
        // Legacy single version or production version
        technicalVersion = version;
        releaseVersion = version.startsWith('v') ? version : `${new Date().toISOString().slice(0, 10).replace(/-/g, '.')}`;
      }

      // Check if we need to update (use technical version for comparison)
      const currentTechnicalVersion = localStorage.getItem(TECHNICAL_VERSION_KEY);
      if (!currentTechnicalVersion || technicalVersion !== currentTechnicalVersion) {
        console.log(`New version detected: ${technicalVersion}. Current: ${currentTechnicalVersion}`);
        
        // Show update overlay (copy is user-friendly in index.html)
        const overlay = document.getElementById('version-update-overlay');
        if (overlay && window.showVersionUpdateOverlay) {
          window.showVersionUpdateOverlay('We\'ve made some improvements. One moment while we get everything ready.');
        } else if (overlay) {
          overlay.style.display = 'flex';
        }
        
        // Recorded before clearing so a cache layer that never settles cannot
        // strand the client on an unknown version forever. IndexedDB can hang
        // indefinitely when a connection is blocked by another tab, and a hang
        // is not a rejection, so try/catch alone would not release this.
        localStorage.setItem(VERSION_KEY, version); // Keep full version for debugging
        localStorage.setItem(RELEASE_VERSION_KEY, releaseVersion);
        localStorage.setItem(TECHNICAL_VERSION_KEY, technicalVersion);
        localStorage.setItem(LAST_UPDATE_KEY, Date.now().toString());

        // Set a flag to indicate update in progress with timestamp
        localStorage.setItem('update_in_progress', Date.now().toString());

        try {
          // Clear caches
          await withTimeout(
            Promise.all([
              schoolCache.clearAllCache(),
              districtCache.clearAllCache()
            ]),
            CACHE_CLEAR_TIMEOUT
          );
        } catch (cacheError) {
          console.warn('Cache clearing failed:', cacheError);
        }
        
        // Reload the page to pick up the new asset bundle
        setTimeout(() => {
          window.location.reload();
        }, 500);
        
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error checking app version:', error);
      
      // Hide overlay if error occurs
      if (window.hideVersionUpdateOverlay) {
        window.hideVersionUpdateOverlay();
      } else {
        const overlay = document.getElementById('version-update-overlay');
        if (overlay) {
          overlay.style.display = 'none';
        }
        localStorage.removeItem('update_in_progress');
      }
      
      return false;
    } finally {
      checkInProgress = false;
    }
  })();
  
  return checkPromise;
}

/**
 * Start periodic version checking
 * @returns {Function} Cleanup function to stop interval
 */
export function startVersionCheckInterval() {
  // Clear existing interval if any
  if (versionCheckInterval) {
    clearInterval(versionCheckInterval);
    versionCheckInterval = null;
  }
  
  // Check if an update has been in progress for too long (30 seconds)
  const updateInProgress = localStorage.getItem('update_in_progress');
  if (updateInProgress) {
    const updateStartTime = parseInt(updateInProgress);
    const currentTime = Date.now();
    
    if (isNaN(updateStartTime) || currentTime - updateStartTime > UPDATE_TIMEOUT) {
      console.warn('Update process timed out or stuck, resetting update state');
      if (window.hideVersionUpdateOverlay) {
        window.hideVersionUpdateOverlay();
      } else {
        localStorage.removeItem('update_in_progress');
        const overlay = document.getElementById('version-update-overlay');
        if (overlay) {
          overlay.style.display = 'none';
        }
        document.documentElement.classList.remove('show-version-overlay');
      }
    }
  }
  
  // Overlay visibility after reload is handled by index.html + useVersionHandler (single hide with fade)
  
  // Trigger a check when the user returns to the tab
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      checkAppVersion().catch(error => {
        console.error('Visibility change version check failed:', error);
      });
    }
  };

  // Trigger a check when the browser window regains focus (e.g. alt-tab back)
  const handleFocus = () => {
    checkAppVersion().catch(error => {
      console.error('Window focus version check failed:', error);
    });
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('focus', handleFocus);

  // Initial check
  checkAppVersion().catch(error => {
    console.error('Initial version check failed:', error);
  });
  
  // Set up interval
  versionCheckInterval = setInterval(() => {
    checkAppVersion().catch(error => {
      console.error('Periodic version check failed:', error);
    });
  }, CHECK_INTERVAL);
  
  // Return cleanup function
  return () => {
    if (versionCheckInterval) {
      clearInterval(versionCheckInterval);
      versionCheckInterval = null;
    }
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('focus', handleFocus);
  };
}

/**
 * Hook for React components to use for route change checks
 * @returns {Object} Version check functions
 */
export function useVersionCheck() {
  return {
    checkOnRouteChange: () => {
      checkAppVersion().catch(error => {
        console.error('Route change version check failed:', error);
      });
    }
  };
}